import type { DataUpload, TilesetAnchor, TilesetShOption, TilesetType } from '@/common/data-upload';
import { t } from '@/i18n';
import { axios } from '@/lib/utils/axios';

import { getResponse } from '../common';
import { API_URLS } from '../query.client';
import { PRESIGNED_UPLOAD_SCOPE, uploadFileWithPresignedUrl } from '../uploads/presigned-upload';

import { getTilesetCapabilities } from './get-tileset-capabilities';

export type UploadTilesetParams = {
	file: File;
	type: TilesetType;
	// Gaussian splat spherical-harmonics option (only sent for gaussian-splat)
	sh?: TilesetShOption;
	name?: string;
	assetId: string;
	subAssetId?: string;
	inspectionId?: string;
	damageId?: string;
	// Initial position (WGS84). When both are given the backend marks the tileset
	// positioned and the converter places it there; otherwise it is placed later
	// via Set Location.
	lon?: number;
	lat?: number;
	// Default placement metadata (persisted; not a real position until Set Location)
	height?: number;
	anchor?: TilesetAnchor;
	// Progress + cancellation, used by the background upload manager.
	// Note: onUploadProgress reflects browser->backend transfer only.
	onUploadProgress?: (percent: number) => void;
	signal?: AbortSignal;
};

/**
 * Uploads a model and records a TILESET data upload. This is a "basic upload"
 * (no location); positioning is done later via setTilesetLocation. Returns the
 * created record (status: queued).
 *
 * The backend is still asked first: a deployment with conversion switched off
 * has nowhere to put the file, and failing here beats a record stuck in queued.
 */
export async function uploadTileset(params: UploadTilesetParams): Promise<DataUpload | null> {
	const capabilities = await getTilesetCapabilities();
	if (capabilities?.enabled === false) {
		throw new Error(t('files.upload.conversionUnavailable'));
	}
	return uploadTilesetDirect(params);
}

/**
 * The browser PUTs the source straight to S3 and the backend is only told the
 * key. A ~1 GB model never passes through it, which is what made this class of
 * upload an out-of-memory kill on the service that was replaced.
 *
 * Two calls rather than one on purpose: an upload the user abandons leaves an
 * unreferenced S3 object instead of a record stuck in `queued` with nothing
 * behind it.
 */
async function uploadTilesetDirect(params: UploadTilesetParams): Promise<DataUpload | null> {
	const { key } = await uploadFileWithPresignedUrl(
		params.file,
		{ scope: PRESIGNED_UPLOAD_SCOPE.TILESET, assetId: params.assetId },
		{ onProgress: params.onUploadProgress, signal: params.signal },
	);

	const response = await axios.post(
		API_URLS.DATA_UPLOAD.TILESET_JOB,
		{
			type: params.type,
			sourceKey: key,
			filename: params.file.name,
			name: params.name,
			assetId: params.assetId,
			subAssetId: params.subAssetId,
			inspectionId: params.inspectionId,
			damageId: params.damageId,
			lon: params.lon,
			lat: params.lat,
			height: params.height,
			anchor: params.anchor,
			// Only the splat SH option is exposed to users today; everything else
			// comes from the type preset on the backend.
			...(params.sh ? { options: { sh: params.sh } } : {}),
		},
		{ signal: params.signal },
	);
	return getResponse<DataUpload>(response.data);
}
